import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import FingerprintJS from '@fingerprintjs/fingerprintjs';
import { usePendingPayment } from '../hooks/usePendingPayment';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SubscriptionContext = createContext(null);

export function SubscriptionProvider({ children }) {
  const [visitorId, setVisitorId] = useState(null);
  const [isPremium, setIsPremium] = useState(false);
  const [usage, setUsage] = useState({
    dice: { used: 0, limit: 10, remaining: 10 },
    tod: { used: 0, limit: 10, remaining: 10 },
    positions: { used: 0, limit: 5, remaining: 5 }
  });
  const [subscriptionEnd, setSubscriptionEnd] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPaywall, setShowPaywall] = useState(false);
  const [paywallFeature, setPaywallFeature] = useState(null);
  const [userEmail, setUserEmail] = useState(() => localStorage.getItem('lp_email') || null);

  // Initialize fingerprint and fetch usage
  useEffect(() => {
    const init = async () => {
      try {
        // Get device fingerprint - use localStorage cache for stability
        let visId = localStorage.getItem('lp_vid');
        if (!visId) {
          const fp = await FingerprintJS.load();
          const result = await fp.get();
          visId = result.visitorId;
          localStorage.setItem('lp_vid', visId);
        }
        setVisitorId(visId);

        // Fetch usage from API
        const response = await fetch(`${API_URL}/api/usage/check`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ visitor_id: visId })
        });

        if (response.ok) {
          const data = await response.json();
          setIsPremium(data.is_premium);
          setUsage(data.usage);
          setSubscriptionEnd(data.subscription_end);
        }
      } catch (error) {
        console.error('Failed to initialize subscription:', error);
      } finally {
        setLoading(false);
      }
    };

    init();
  }, []);

  // Check and use a feature (returns true if allowed, false if blocked)
  const checkAndUseFeature = useCallback(async (feature) => {
    if (!visitorId) return false;

    // If premium, always allow
    if (isPremium) return true;

    // Check remaining uses
    if (usage[feature]?.remaining <= 0) {
      setPaywallFeature(feature);
      setShowPaywall(true);
      return false;
    }

    try {
      const response = await fetch(`${API_URL}/api/usage/increment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ visitor_id: visitorId, feature })
      });

      if (response.ok) {
        const data = await response.json();
        
        if (!data.success) {
          setPaywallFeature(feature);
          setShowPaywall(true);
          return false;
        }

        // Update local usage
        setUsage(prev => ({
          ...prev,
          [feature]: {
            ...prev[feature],
            used: prev[feature].used + 1,
            remaining: data.remaining
          }
        }));

        return true;
      }
    } catch (error) {
      console.error('Failed to use feature:', error);
    }

    return false;
  }, [visitorId, isPremium, usage]);

  // Create subscription
  const createSubscription = useCallback(async (email, phone) => {
    if (!visitorId) return null;

    try {
      const response = await fetch(`${API_URL}/api/subscription/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ visitor_id: visitorId, email, phone })
      });

      if (response.ok) {
        return await response.json();
      }
    } catch (error) {
      console.error('Failed to create subscription:', error);
    }

    return null;
  }, [visitorId]);

  // Verify payment
  const verifyPayment = useCallback(async (paymentData) => {
    if (!visitorId) return false;

    try {
      const response = await fetch(`${API_URL}/api/subscription/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          visitor_id: visitorId,
          razorpay_payment_id: paymentData.razorpay_payment_id,
          razorpay_subscription_id: paymentData.razorpay_subscription_id,
          razorpay_signature: paymentData.razorpay_signature
        })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setIsPremium(true);
          setSubscriptionEnd(data.subscription_end);
          setShowPaywall(false);
          return true;
        }
      }
    } catch (error) {
      console.error('Failed to verify payment:', error);
    }

    return false;
  }, [visitorId]);

  // Refresh usage from server
  const refreshUsage = useCallback(async () => {
    if (!visitorId) return;

    try {
      const response = await fetch(`${API_URL}/api/usage/check`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ visitor_id: visitorId })
      });

      if (response.ok) {
        const data = await response.json();
        setIsPremium(data.is_premium);
        setUsage(data.usage);
        setSubscriptionEnd(data.subscription_end);
        return data;
      }
    } catch (error) {
      console.error('Failed to refresh usage:', error);
    }
  }, [visitorId]);

  const paymentState = usePendingPayment(visitorId, refreshUsage, setShowPaywall);

  useEffect(() => {
    const onVisible = () => { if (document.visibilityState === 'visible') refreshUsage(); };
    window.addEventListener('focus', onVisible);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('focus', onVisible);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [refreshUsage]);

  // Link email to current device for progress persistence across reinstalls
  const linkEmail = useCallback(async (email) => {
    if (!visitorId || !email) return { success: false };

    try {
      const response = await fetch(`${API_URL}/api/auth/email-link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ visitor_id: visitorId, email })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          localStorage.setItem('lp_email', email.toLowerCase().trim());
          setUserEmail(email.toLowerCase().trim());
          if (data.restored) {
            setIsPremium(data.is_premium || false);
            if (data.usage) setUsage(data.usage);
            if (data.subscription_end) setSubscriptionEnd(data.subscription_end);
          }
        }
        return data;
      }
    } catch (error) {
      console.error('Failed to link email:', error);
    }
    return { success: false };
  }, [visitorId]);

  const value = {
    ...paymentState,
    visitorId,
    isPremium,
    usage,
    subscriptionEnd,
    loading,
    showPaywall,
    setShowPaywall,
    paywallFeature,
    setPaywallFeature,
    checkAndUseFeature,
    createSubscription,
    verifyPayment,
    refreshUsage,
    userEmail,
    linkEmail
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
}

export function useSubscription() {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
}
